import type { BeforeSendMiddleware } from '@vercel/speed-insights'
import { SpeedInsights } from '@vercel/speed-insights/next'
import type { NextPage } from 'next'
import { withPasswordProtect } from 'next-password-protect'
import type { AppProps } from 'next/app'
import { type ReactElement, type ReactNode, useEffect } from 'react'
import { CookiesProvider } from 'react-cookie'
import { ThemeProvider } from 'styled-components'

import '@/styles/fonts.css'
import { AttributionProvider } from '@/context/attributionContext'
import { AuthContextProvider } from '@/context/authContext'
import { BannerProvider } from '@/context/bannerContext'
import { BasketProvider } from '@/context/basketContext'
import { BrazeProvider } from '@/context/brazeContext'
import { CookieConsentContextProvider } from '@/context/cookieConsentContext'
import { GeoContextProvider } from '@/context/geoContext'
import { LeadProvider } from '@/context/leadContext'
import { LocaleProvider } from '@/context/localeContext'
import { OptimizelyProvider } from '@/context/optimizelyContext'
import { ProductAvailabilityProvider } from '@/context/productAvailabilityContext'
import { RewardsProvider } from '@/context/rewardsContext'
import { TrackingProvider } from '@/context/trackingContext'

import { EmbeddedMobileAppLayout } from '@/components/pages/embedded-mobile-app/EmbeddedMobileAppLayout'
import { PurchaseTracker } from '@/components/shared/tracking/PurchaseTracker'

import { isEmbeddedMobileAppRoute } from '@/lib/app-embed/isEmbeddedMobileAppRoute'
import { hasAnalyticsConsent } from '@/lib/consent'
import { useBrazeExperimentSync } from '@/lib/hooks/useBrazeExperimentSync'
import { useBrazePageTracking } from '@/lib/hooks/useBrazePageTracking'
import { useExperimentAssignmentsCapture } from '@/lib/hooks/useExperimentAssignmentsCapture'

import { GlobalStyles } from '@/styles/globalStyles'
import { theme } from '@/styles/theme'

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

// Speed Insights bills per event, and the logged-in account area isn't part of
// the acquisition journey we're measuring.
const skipAccountPages: BeforeSendMiddleware = (event) =>
  new URL(event.url, window.location.origin).pathname.startsWith('/account') ? null : event

function BrazePageTracker() {
  useBrazePageTracking()
  return null
}

// Mirrors Optimizely experiment assignments onto the Braze profile. Must sit
// inside OptimizelyProvider (reads flags) and BrazeProvider (sets attributes).
function BrazeExperimentSync() {
  useBrazeExperimentSync()
  return null
}

// Mirrors Optimizely experiment assignments into the in-memory store so the
// attribution calls can attach them. Must sit inside OptimizelyProvider.
function ExperimentAssignmentsCapture() {
  useExperimentAssignmentsCapture()
  return null
}

function WebsiteApp({ Component, pageProps }: AppPropsWithLayout) {
  const ENABLE_OPTIMIZELY = process.env.ENABLE_OPTIMIZELY === 'true'
  const ENABLE_SPEED_INSIGHTS = process.env.NEXT_PUBLIC_ZOE_ENV === 'PROD'

  // Dispatch custom event when Optimizely assigns a variation (regardless of analytics consent)
  useEffect(() => {
    if (!ENABLE_OPTIMIZELY || typeof window === 'undefined') {
      return
    }

    const variationAssignedHandler = (event: unknown) => {
      window.dispatchEvent(
        new CustomEvent('optimizelyVariationAssigned', {
          detail: event,
        }),
      )
    }

    const listenerConfig = {
      type: 'addListener',
      filter: {
        type: 'lifecycle',
        name: 'campaignDecided',
      },
      handler: variationAssignedHandler,
    }

    window['optimizely'] = window['optimizely'] || []
    window['optimizely'].push(listenerConfig)

    return () => {
      window['optimizely']?.push({
        type: 'removeListener',
        filter: {
          type: 'lifecycle',
          name: 'campaignDecided',
        },
        handler: variationAssignedHandler,
      })
    }
  }, [])

  const getLayout = Component.getLayout ?? ((page) => page)
  return (
    <>
      <AuthContextProvider>
        <CookiesProvider>
          <GeoContextProvider>
            <BrazeProvider>
              <BrazePageTracker />
              <OptimizelyProvider enabled={ENABLE_OPTIMIZELY} hasConsent={hasAnalyticsConsent()}>
                <BrazeExperimentSync />
                <ExperimentAssignmentsCapture />
                <TrackingProvider>
                  <CookieConsentContextProvider>
                    <AttributionProvider>
                      <LeadProvider>
                        <PurchaseTracker />
                        <BasketProvider>
                          <LocaleProvider>
                            <ProductAvailabilityProvider>
                              <RewardsProvider>
                                <BannerProvider>
                                  <GlobalStyles />
                                  <ThemeProvider theme={theme}>{getLayout(<Component {...pageProps} />)}</ThemeProvider>
                                </BannerProvider>
                              </RewardsProvider>
                            </ProductAvailabilityProvider>
                          </LocaleProvider>
                        </BasketProvider>
                      </LeadProvider>
                    </AttributionProvider>
                  </CookieConsentContextProvider>
                </TrackingProvider>
              </OptimizelyProvider>
            </BrazeProvider>
          </GeoContextProvider>
        </CookiesProvider>
      </AuthContextProvider>
      {ENABLE_SPEED_INSIGHTS && <SpeedInsights sampleRate={0.5} beforeSend={skipAccountPages} />}
    </>
  )
}

function App(props: AppPropsWithLayout) {
  const { Component, pageProps, router } = props
  if (isEmbeddedMobileAppRoute(router.pathname)) {
    return (
      <EmbeddedMobileAppLayout>
        <Component {...pageProps} />
      </EmbeddedMobileAppLayout>
    )
  }
  return <WebsiteApp {...props} />
}

export default process.env.PASSWORD_PROTECTED_ENVIRONMENT === String(true)
  ? withPasswordProtect(App, {
      loginComponentProps: {
        buttonBackgroundColor: '#FA7268',
        buttonColor: '#FFFFFF',
        logo: '/assets/img/zoe-logo.svg',
      },
    })
  : App
